import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Copy, FilePlus, Sparkle, Stack, Trash, X } from "@phosphor-icons/react";
import { MS_FILE_TYPES, PDF_FILE_TYPES } from "../../utils/constants";

// Selection action bar for the matter documents table. Appears when rows
// are checked.
//
// Eligibility differs by action, which is why there are two helpers:
// merge and Page Builder accept Office files because the server-side
// assembly flow converts them on the way in, while AI prepare reads PDFs
// only.

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 8px;
  background: #f1f6fe;
  border: 1px solid #c8dcf7;
  border-radius: 8px;
  font-family: "Manrope", sans-serif;
  font-size: 13px;
  color: #48566b;
`;

const CountText = styled.span`
  font-weight: 600;
`;

const IneligibleNote = styled.span`
  color: #8a97a8;
`;

const MergeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 6px;
  background: #0c53bb;
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  cursor: pointer;

  &:disabled {
    background: #aeb8c4;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled(MergeButton)`
  background: #ffffff;
  color: #dc2626;
  border: 1px solid #fca5a5;

  &:disabled {
    background: #ffffff;
    color: #cbd5e1;
    border-color: #e5e7eb;
  }
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  color: #48566b;
  font-family: inherit;
  font-size: 13px;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: #e3eefd;
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

export const isMergeEligibleRecord = (record) =>
  !!record &&
  record.type !== "folder" &&
  (PDF_FILE_TYPES.includes(record.file_type) ||
    MS_FILE_TYPES.includes(record.file_type));

// Map the table's selectedRowKeys back to their records, keeping only
// merge-eligible documents. Key derivation mirrors the shared Table's
// rowKey: record.id, falling back to key, then unique_id.
export const getMergeEligibleRecords = (selectedRowKeys, rows) => {
  const keys = new Set((selectedRowKeys || []).map((key) => String(key)));
  return (rows || []).filter((record) => {
    if (!isMergeEligibleRecord(record)) return false;
    const rowKey = String(record.id ?? record.key ?? record.unique_id ?? "");
    return rowKey !== "" && keys.has(rowKey);
  });
};

export const getSelectedDocumentRecords = (selectedRowKeys, rows) => {
  const keys = new Set((selectedRowKeys || []).map((key) => String(key)));
  return (rows || []).filter((record) => {
    if (!record || record.type === "folder") return false;
    const rowKey = String(record.id ?? record.key ?? record.unique_id ?? "");
    return rowKey !== "" && keys.has(rowKey);
  });
};

// AI prepare reads PDFs. Office files would need conversion first, which
// the prepare pipeline does not do, so they are excluded here rather than
// enqueued and failed one by one.
export const getAiEligibleRecords = (selectedRowKeys, rows) =>
  getSelectedDocumentRecords(selectedRowKeys, rows).filter((record) =>
    PDF_FILE_TYPES.includes(record.file_type)
  );

const BuilderButton = styled(MergeButton)`
  background: #ffffff;
  color: #0c53bb;
  border: 1px solid #0c53bb;

  &:disabled {
    background: #ffffff;
    color: #aeb8c4;
    border-color: #aeb8c4;
  }
`;

const AiButton = styled(MergeButton)`
  background: #ffffff;
  color: #6d28d9;
  border: 1px solid #c4b5fd;

  &:disabled {
    background: #ffffff;
    color: #cbd5e1;
    border-color: #e5e7eb;
  }
`;

const MergeSelectedBar = ({
  selectedRowKeys,
  rows,
  onMerge,
  onDelete,
  onClear,
  onPageBuilder,
  onPrepareForAi,
  onSaveAsCollection,
  onCopy,
  preparingForAi,
}) => {
  const selectedCount = selectedRowKeys?.length || 0;
  if (selectedCount === 0) return null;

  const eligible = getMergeEligibleRecords(selectedRowKeys, rows);
  const selectedDocuments = getSelectedDocumentRecords(selectedRowKeys, rows);
  const aiEligible = getAiEligibleRecords(selectedRowKeys, rows);
  const aiTitle =
    aiEligible.length < 1 ? "Select at least one PDF" : undefined;
  // Keys that don't resolve against the CURRENT page's rows (selection can
  // outlive pagination via the tab-switch restore path). They can't be
  // merged — say so instead of letting the headline count overpromise.
  const currentPageKeys = new Set(
    (rows || [])
      .map((record) =>
        String(record?.id ?? record?.key ?? record?.unique_id ?? "")
      )
      .filter((key) => key !== "")
  );
  const offPageCount = (selectedRowKeys || []).filter(
    (key) => !currentPageKeys.has(String(key))
  ).length;
  const ineligibleCount = selectedCount - eligible.length - offPageCount;

  return (
    <Bar data-testid="merge-selected-bar">
      <CountText data-testid="merge-selected-count">
        {selectedCount} selected
      </CountText>
      {ineligibleCount > 0 && (
        <IneligibleNote data-testid="merge-ineligible-note">
          ({ineligibleCount} can&apos;t be merged — only PDF and Office files)
        </IneligibleNote>
      )}
      {offPageCount > 0 && (
        <IneligibleNote data-testid="merge-offpage-note">
          ({offPageCount} on other pages won&apos;t be included)
        </IneligibleNote>
      )}
      <Spacer />
      {typeof onCopy === "function" && (
        <BuilderButton
          type="button"
          data-testid="copy-selected-button"
          disabled={selectedDocuments.length < 1}
          title={
            selectedDocuments.length < 1
              ? "Select at least one document to copy"
              : "Copy these files, then paste them into another matter"
          }
          onClick={() => onCopy(selectedDocuments)}
        >
          <Copy size={16} weight="regular" />
          Copy {selectedDocuments.length || ""}
        </BuilderButton>
      )}
      {typeof onSaveAsCollection === "function" && (
        <AiButton
          type="button"
          data-testid="save-collection-button"
          disabled={aiEligible.length < 1}
          title={
            aiTitle ||
            "Name this group so you can ask questions across all of it"
          }
          onClick={() => onSaveAsCollection(aiEligible)}
        >
          <Stack size={16} weight="regular" />
          Save as collection
        </AiButton>
      )}
      {typeof onPrepareForAi === "function" && (
        <AiButton
          type="button"
          data-testid="prepare-ai-selected-button"
          disabled={aiEligible.length < 1 || preparingForAi}
          title={aiTitle || "Extract and index these PDFs for AI chat"}
          onClick={() => onPrepareForAi(aiEligible)}
        >
          <Sparkle size={16} weight="regular" />
          {preparingForAi
            ? "Starting…"
            : `Prepare ${aiEligible.length || ""} for AI`}
        </AiButton>
      )}
      {typeof onPageBuilder === "function" && (
        <BuilderButton
          type="button"
          data-testid="page-builder-selected-button"
          disabled={eligible.length < 1}
          title={
            eligible.length < 1
              ? "Select at least one PDF or Office file"
              : "Pick pages and ranges, drag them into order, and build a new PDF"
          }
          onClick={() => onPageBuilder(eligible)}
        >
          <FilePlus size={16} weight="regular" />
          Page Builder
        </BuilderButton>
      )}
      <MergeButton
        type="button"
        data-testid="merge-selected-button"
        disabled={eligible.length < 2}
        title={
          eligible.length < 2
            ? "Select at least two PDF or Office files to merge"
            : undefined
        }
        onClick={() => onMerge(eligible)}
      >
        <FilePlus size={16} weight="regular" />
        Merge {eligible.length >= 2 ? eligible.length : ""} into one PDF
      </MergeButton>
      {typeof onDelete === "function" && (
        <DeleteButton
          type="button"
          data-testid="delete-selected-button"
          disabled={selectedDocuments.length < 1}
          title={
            selectedDocuments.length < 1
              ? "Select at least one document to delete"
              : undefined
          }
          onClick={() => onDelete(selectedDocuments)}
        >
          <Trash size={16} weight="regular" />
          Delete {selectedDocuments.length || ""}
        </DeleteButton>
      )}
      <ClearButton
        type="button"
        data-testid="merge-selected-clear"
        onClick={onClear}
      >
        <X size={14} />
        Clear
      </ClearButton>
    </Bar>
  );
};

MergeSelectedBar.propTypes = {
  selectedRowKeys: PropTypes.array,
  rows: PropTypes.array,
  onMerge: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onClear: PropTypes.func.isRequired,
  onPageBuilder: PropTypes.func,
  onPrepareForAi: PropTypes.func,
  onSaveAsCollection: PropTypes.func,
  onCopy: PropTypes.func,
  preparingForAi: PropTypes.bool,
};

export default MergeSelectedBar;
